import React, { useState } from 'react';
import { Grid, Button, TextField } from '@mui/material';
import {
	createDepartment,
	SchedulerDepartment,
} from '../../../../core/API/scheduler';
import {
	isDepartmentNameValid,
	MAX_DEPARTMENTS,
} from '../../../../module/schedule/utils/scheduleDepartmentUtils';
import styles from '../SchedulerSetting.module.scss';

interface CreateDepartmentProps {
	backButtonClick: () => void;
	onDepartmentCreated?: () => void;
	existingDepartments: SchedulerDepartment[];
}

const CreateDepartment: React.FC<CreateDepartmentProps> = ({
	backButtonClick,
	onDepartmentCreated,
	existingDepartments,
}) => {
	const [name, setName] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (existingDepartments.length >= MAX_DEPARTMENTS) {
			setError(`Maximum of ${MAX_DEPARTMENTS} departments allowed`);
			return;
		}

		const validationError = isDepartmentNameValid(name, existingDepartments);
		if (validationError) {
			setError(validationError);
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			await createDepartment({ name: name.trim() });
			onDepartmentCreated?.();
			setTimeout(() => {
				backButtonClick();
			}, 1500);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.['hydra:description'] ||
				err?.response?.data?.message ||
				(err instanceof Error ? err.message : 'Failed to create department');
			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.createJobCode}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={backButtonClick}>
						Back
					</Button>
					<Button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Create New Department</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Department Name"
					name="name"
					placeholder="Enter department name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>
			</form>
		</div>
	);
};

export default CreateDepartment;
